import { Box, Card, Stack, styled, useMediaQuery, useTheme } from '@mui/material'
import { MUIThemeColors } from 'src/mui-theme'
import { GetRGBA, getThemeColor } from 'src/utils'
import { ReactComponent as UserIcon } from '../../assets/icons/userIcon.svg'
import { IProjectStatus } from 'src/generated-types/project-status'
import { IProjectInfo } from 'src/generated-types/project'
import { useTranslation } from 'react-i18next'
import { IUser } from 'src/generated-types'
import { DynamicTextTypography } from '../DynamicTextTypography'
import ProjectStatusChips from 'src/screens/project/versions/ProjectStatusChips'

interface ProjectHeaderProps {
  project: IProjectInfo
  classname?: string
  onClick: (e: any) => void
  status?: IProjectStatus
  active?: boolean
  isLoading?: boolean
  customerId: EntityId
}
const ProjectHeader = ({ project, classname, onClick, active }: ProjectHeaderProps) => {
  const theme = useTheme()
  const isMobile = useMediaQuery<any>(theme.breakpoints.down('sm'))
  const { t } = useTranslation('projects')

  const formatUserName = (user: IUser) =>
    user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email
  const formatUserContact = (user: IUser) => (user.phoneNumber ? ` | ${user.phoneNumber}` : '')

  return (
    <Box
      onClick={onClick}
      className={classname}
      width={'100%'}
      position={'relative'}
      margin={isMobile ? '0px' : '0px 0px 10px 0px'}
      sx={{
        borderRadius: '4px',
        border: active ? `2px solid ${getThemeColor(theme, MUIThemeColors.primaryMain)}` : 'none',
      }}
    >
      <ProjectHeaderCard
        style={{ height: isMobile ? '110px' : '100px' }}
        sx={{
          backgroundImage: `url('${process.env.REACT_APP_API_URL}/api/Puppeteer/screenshot/${project.customerId}/${project.localityId}/project/${project.id}'), url('/icons/card-background.png')`,
        }}
      >
        <Box flex={1} paddingTop={isMobile ? '12px' : '6px'}>
          <Stack
            direction={'column'}
            className="pl-12-core pt-2-core pr-6-core pb-2-core"
            alignItems={'flex-start'}
            justifyContent={'center'}
            sx={{
              backgroundColor: getThemeColor(theme, MUIThemeColors.primaryMain),
              width: isMobile ? '90%' : '75%',
              height: isMobile ? '55px' : '54px',
              borderRadius: isMobile ? '0 4px 4px 0' : '0',
            }}
          >
            <DynamicTextTypography
              variant={'body1'}
              fontWeight={600}
              textTransform="uppercase"
              marginBottom="2px"
              lineHeight={isMobile ? '14px' : '18px'}
              color={getThemeColor(theme, MUIThemeColors.white)}
              text={project.name}
              maxLength={isMobile ? 12 : 18}
            />
            <Box display={'flex'} gap={'4px'} alignItems={'center'}>
              <Box sx={{ '& svg': { width: isMobile ? '9px' : '12px', height: isMobile ? '9px' : '12px' } }}>
                <UserIcon />
              </Box>
              <Box>
                <DynamicTextTypography
                  fontWeight={200}
                  lineHeight={isMobile ? '13px' : '12px'}
                  variant="caption"
                  color={getThemeColor(theme, MUIThemeColors.white)}
                  display={'flex'}
                  alignItems={'center'}
                  text={
                    project?.customerContact
                      ? `${formatUserName(project?.customerContact)} ${formatUserContact(project?.customerContact)}`
                      : 'N/A'
                  }
                  maxLength={isMobile ? 20 : 25}
                />
              </Box>
            </Box>

            <DynamicTextTypography
              fontWeight={200}
              lineHeight={'12px'}
              variant="caption"
              color={getThemeColor(theme, MUIThemeColors.white)}
              paddingTop={'3px'}
              text={`${t('our-references', { ns: 'projects' })} : ${formatUserName(project?.ourContact)}`}
              maxLength={isMobile ? 20 : 25}
            />
          </Stack>
        </Box>
        <Box
          paddingTop={isMobile ? '0px' : '10px'}
          flex={1}
          justifyItems={'flex-end'}
          sx={
            isMobile
              ? {
                  position: 'absolute',
                  bottom: '13px',
                  right: 0,
                }
              : {}
          }
        >
          <ProjectStatusChips project={project} variant="card" />
        </Box>
      </ProjectHeaderCard>
    </Box>
  )
}

export default ProjectHeader

const ProjectHeaderCard = styled(Card)(({ theme }) => ({
  '&.MuiPaper-root': {
    backgroundColor: theme.palette.white[theme.palette.mode],
    color: theme.palette.primaryDark[theme.palette.mode],
    boxShadow: `0px 2px 4px ${GetRGBA(theme.palette.black[theme.palette.mode], 0.1)}`,
  },
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  cursor: 'pointer',
  color: theme.palette.white[theme.palette.mode],
  position: 'relative',
  borderRadius: '4px',
  border: '0.5px solid rgba(0,0,0,0.12)',
}))
